import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Clock, User, RefreshCw, Home as HomeIcon, CalendarPlus, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { usePageMeta } from '@/hooks/usePageMeta';

// Dedicated DJ application status page (Chunk 2). Authenticated-only. ALWAYS
// fetches the latest status from GET /api/users/me/dj-application on open — never
// from stale AuthContext/localStorage — so an admin approve/reject/remove/archive/
// restore is reflected on reload. Old application history is never shown.
const REAPPLY_STATUSES = ['rejected', 'removed', 'archived'];

export default function DJStatus() {
  usePageMeta('DJ Status', 'Your DJ application status on SoundWolves.');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);
  const [galleryNotice, setGalleryNotice] = useState(null);   // private review note
  const [redirect, setRedirect] = useState(null);   // '/login' | '/' | null

  useEffect(() => {
    // Logged out -> login (token check is synchronous + reliable).
    if (!api.isAuthenticated()) { setRedirect('/login'); setLoading(false); return; }
    let active = true;
    setLoading(true);
    api.get('/users/me/dj-application')
      .then((app) => {
        if (!active) return;
        setStatus(app?.status || null);
        setProfileUrl(app?.public_profile_url || null);
        setGalleryNotice(app?.gallery_notice || null);
        setLoading(false);
      })
      .catch(() => {
        // 404 (or any error) => no application: go home, no apply CTA here.
        if (active) { setRedirect('/'); setLoading(false); }
      });
    return () => { active = false; };
  }, []);

  if (redirect) return <Navigate to={redirect} replace />;
  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex items-center justify-center" data-testid="dj-status-loading">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  let icon = Clock, heading = '', action = null;
  if (status === 'pending') {
    icon = Clock;
    heading = 'Your DJ application is under review';
    action = (
      <Link to="/"><Button variant="outline" data-testid="dj-status-home"><HomeIcon className="w-4 h-4 mr-2" />Back to Home</Button></Link>
    );
  } else if (status === 'approved') {
    icon = User;
    heading = 'Your DJ profile is approved and live';
    action = (
      <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
        {profileUrl && (
          <Link to={profileUrl}><Button variant="premium" data-testid="dj-status-profile"><User className="w-4 h-4 mr-2" />View My DJ Profile</Button></Link>
        )}
        <Link to="/dj/events"><Button variant="premium" data-testid="dj-status-create-event"><CalendarPlus className="w-4 h-4 mr-2" />Create Event</Button></Link>
        <Link to="/dj/events"><Button variant="outline" data-testid="dj-status-my-events"><CalendarDays className="w-4 h-4 mr-2" />My Events</Button></Link>
      </div>
    );
  } else if (REAPPLY_STATUSES.includes(status)) {
    icon = RefreshCw;
    heading = 'Your DJ profile is no longer active. You can reapply anytime.';
    action = (
      <Link to="/dj-application"><Button variant="premium" data-testid="dj-status-reapply"><RefreshCw className="w-4 h-4 mr-2" />Reapply as a DJ</Button></Link>
    );
  } else {
    // Unknown/unexpected status — safe fallback, no history exposed.
    icon = Clock;
    heading = 'Your DJ application status is unavailable.';
    action = <Link to="/"><Button variant="outline"><HomeIcon className="w-4 h-4 mr-2" />Back to Home</Button></Link>;
  }
  const Icon = icon;

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8" data-testid="dj-status-page">
      <div className="container mx-auto px-4 max-w-xl">
        <Card className="glass-card border-border/50">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 mx-auto mb-5 flex items-center justify-center">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold mb-6" data-testid="dj-status-heading">{heading}</h1>
            {galleryNotice && (
              <div className="mb-6 mx-auto max-w-md rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700" data-testid="dj-status-gallery-notice">
                {galleryNotice}
              </div>
            )}
            <div className="flex justify-center">{action}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
